import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { ProviderLogo } from '../components/ProviderLogo';
import { useApp } from '../context/AppContext';
import { appUrlForRegion, oppositeRegion, regionDisplayName, regionFromRuntime } from '../lib/regionRuntime';
import {
  createEmailAccount,
  friendlyAuthError,
  resetPasswordEmail,
  signInWithEmailPassword,
  signInWithGoogle,
  signInWithMicrosoft,
} from '../services/authService';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignInPage() {
  const { state } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const rawReturnTo = String(searchParams.get('returnTo') || '').trim();
  const returnTo = rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//') ? rawReturnTo : '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('signin');
  const [step, setStep] = useState('email');

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState('');
  const [authSettling, setAuthSettling] = useState(false);

  const cleanEmail = String(email || '').trim().toLowerCase();
  const busy = Boolean(loading) || authSettling || (state.user && !state.initialAppLoaded);
  const isCreateMode = mode === 'create';
  const isPasswordStep = step === 'password';
  const runtimeRegion = regionFromRuntime(state.region || 'global');
  const otherRegion = oppositeRegion(runtimeRegion);

  const environmentLabel = useMemo(
    () => `${regionDisplayName(runtimeRegion)} environment`,
    [runtimeRegion],
  );

  useEffect(() => {
    if (!state.authReady || !state.user || !state.initialAppLoaded) return;
    navigate(returnTo || (state.onboardingComplete ? '/home' : '/setup'), { replace: true });
  }, [state.authReady, state.initialAppLoaded, state.user, state.onboardingComplete, navigate, returnTo]);

  const clearMessages = () => {
    if (error) setError('');
    if (notice) setNotice('');
  };

  const run = async (name, action) => {
    setError('');
    setNotice('');
    setAuthSettling(false);
    setLoading(name);

    try {
      await action();
      setAuthSettling(true);
    } catch (err) {
      setError(friendlyAuthError(err, { mode }));
      setAuthSettling(false);
      setLoading('');
    }
  };

  const continueWithEmail = (event) => {
    event.preventDefault();

    setError('');
    setNotice('');

    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      setError('Enter a valid email address.');
      return;
    }

    setStep('password');
  };

  const submitPassword = async (event) => {
    event.preventDefault();

    setError('');
    setNotice('');

    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      setStep('email');
      setError('Enter a valid email address.');
      return;
    }

    if (!password || password.length < 6) {
      setError(isCreateMode ? 'Use a password with at least 6 characters.' : 'Enter your password.');
      return;
    }

    await run('email', () =>
      isCreateMode
        ? createEmailAccount(cleanEmail, password)
        : signInWithEmailPassword(cleanEmail, password),
    );
  };

  const sendResetLink = async () => {
    setError('');
    setNotice('');

    if (!cleanEmail || !emailRegex.test(cleanEmail)) {
      setStep('email');
      setError('Enter your email first, then we can send a reset link.');
      return;
    }

    setLoading('reset');

    try {
      const result = await resetPasswordEmail(cleanEmail);
      const resetRegion = regionDisplayName(result?.region || runtimeRegion);
      setNotice(
        `If this email has a Snoooz or NudgeForMe account in the ${resetRegion} region, we sent a password reset link. If it does not arrive, check spam or try the other region.`,
      );
    } catch (err) {
      setError(friendlyAuthError(err, { mode: 'reset' }));
    } finally {
      setLoading('');
      setAuthSettling(false);
    }
  };

  const switchMode = () => {
    setMode((current) => (current === 'signin' ? 'create' : 'signin'));
    setPassword('');
    setError('');
    setNotice('');
  };

  const backToEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
    setNotice('');
  };

  const goToOtherRegion = () => {
    const suffix = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    const target = `${appUrlForRegion(otherRegion)}/signin${suffix}`;
    window.location.assign(target);
  };

  const visibleError = error || (state.backendError ? friendlyAuthError(state.backendError) : '');

  return (
    <Box className="app-grid" minHeight="100vh">
      <AppHeader simple />

      <Container maxWidth="sm" sx={{ py: { xs: 7, md: 10 } }}>
        <Typography className="mono-label" color="primary.dark">
          {environmentLabel}
        </Typography>

        <Typography variant="h1" sx={{ mt: 1.5, fontSize: { xs: 40, md: 56 } }}>
          {isPasswordStep
            ? isCreateMode
              ? 'Create account'
              : 'Enter password'
            : 'Sign in'}
        </Typography>

        <Typography color="text.secondary" sx={{ mt: 2, fontSize: 17 }}>
          {isPasswordStep
            ? isCreateMode
              ? 'Create your NudgeForMe password.'
              : 'Use your Snoooz or NudgeForMe password.'
            : 'Use Google, Outlook, or continue with email. If you already use Snoooz, use the same login here.'}
        </Typography>

        {(authSettling || (state.user && !state.initialAppLoaded)) && (
          <Alert
            severity="info"
            icon={<CircularProgress size={18} />}
            sx={{ mt: 3 }}
          >
            Loading your NudgeForMe account…
          </Alert>
        )}

        {notice && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Stack spacing={1}>
              <span>{notice}</span>
              {/* <Box>
                <Button
                  size="small"
                  color="inherit"
                  onClick={goToOtherRegion}
                  disabled={busy}
                  sx={{ px: 0, fontWeight: 800 }}
                >
                  Try {regionDisplayName(otherRegion)} region
                </Button>
              </Box> */}
            </Stack>
          </Alert>
        )}

        {visibleError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <Stack spacing={1}>
              <span>{visibleError}</span>

              {(visibleError.toLowerCase().includes('password') ||
                visibleError.toLowerCase().includes('already has') ||
                visibleError.toLowerCase().includes('already exists')) && (
                <Box>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={sendResetLink}
                    disabled={busy}
                    sx={{ px: 0, fontWeight: 800 }}
                  >
                    Send password reset link
                  </Button>
                </Box>
              )}
            </Stack>
          </Alert>
        )}

        {!isPasswordStep ? (
          <>
            <Stack spacing={1.4} sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                disabled={busy}
                onClick={() => run('google', signInWithGoogle)}
                startIcon={<ProviderLogo provider="google" size={28} />}
                sx={{ justifyContent: 'flex-start', px: 2.2, bgcolor: 'background.paper' }}
              >
                {loading === 'google' ? 'Opening Google…' : 'Continue with Google'}
              </Button>

              <Button
                variant="outlined"
                size="large"
                disabled={busy}
                onClick={() => run('microsoft', signInWithMicrosoft)}
                startIcon={<ProviderLogo provider="microsoft" size={28} />}
                sx={{ justifyContent: 'flex-start', px: 2.2, bgcolor: 'background.paper' }}
              >
                {loading === 'microsoft' ? 'Opening Outlook…' : 'Continue with Outlook'}
              </Button>
            </Stack>

            <Divider sx={{ my: 3.5 }}>or use email</Divider>

            <Box component="form" onSubmit={continueWithEmail}>
              <Stack spacing={2}>
                <TextField
                  label="Work email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearMessages();
                  }}
                  placeholder="you@company.com"
                  fullWidth
                  autoComplete="email"
                  required
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  disabled={busy}
                >
                  Continue with email
                </Button>
              </Stack>
            </Box>

            <Button
              color="inherit"
              onClick={switchMode}
              disabled={busy}
              sx={{ mt: 2, px: 0 }}
            >
              {isCreateMode
                ? 'Already use Snoooz or NudgeForMe? Sign in'
                : 'New to Snoooz and NudgeForMe? Create an account'}
            </Button>
          </>
        ) : (
          <>
            <Box
              sx={{
                mt: 4,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
              }}
            >
              <Typography className="mono-label" color="text.secondary">
                Email
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ mt: 0.5 }}
              >
                <Typography sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                  {cleanEmail}
                </Typography>

                <Button
                  type="button"
                  color="inherit"
                  size="small"
                  onClick={backToEmail}
                  disabled={busy}
                  startIcon={<KeyboardBackspaceRoundedIcon />}
                  sx={{ flexShrink: 0 }}
                >
                  Change
                </Button>
              </Stack>
            </Box>

            <Box component="form" onSubmit={submitPassword} sx={{ mt: 2 }}>
              <Stack spacing={2}>
                <TextField
                  label={isCreateMode ? 'Create password' : 'Password'}
                  type="password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (error) setError('');
                  }}
                  fullWidth
                  autoComplete={isCreateMode ? 'new-password' : 'current-password'}
                  required
                  autoFocus
                  inputProps={{ minLength: 6 }}
                  helperText={isCreateMode ? 'Use at least 6 characters.' : ''}
                />

                {!isCreateMode && (
                  <Box sx={{ mt: -0.5 }}>
                    <Button
                      type="button"
                      color="inherit"
                      onClick={sendResetLink}
                      disabled={busy}
                      sx={{ px: 0, fontSize: 13.5, fontWeight: 800 }}
                    >
                      {loading === 'reset' ? 'Sending reset link…' : 'Forgot password?'}
                    </Button>
                  </Box>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  disabled={busy}
                >
                  {loading === 'email'
                    ? isCreateMode
                      ? 'Creating account…'
                      : 'Signing in…'
                    : isCreateMode
                      ? 'Create account'
                      : 'Sign in'}
                </Button>
              </Stack>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
              <Button
                color="inherit"
                onClick={backToEmail}
                disabled={busy}
                sx={{ px: 0 }}
              >
                Use another method
              </Button>

              <Button
                color="inherit"
                onClick={switchMode}
                disabled={busy}
                sx={{ px: 0 }}
              >
                {isCreateMode ? 'Sign in instead' : 'Create account instead'}
              </Button>
            </Stack>
          </>
        )}

        <Typography color="text.secondary" sx={{ mt: 4, fontSize: 13.5 }}>
          NudgeForMe uses the same secure login system as Snoooz. Password resets are region-specific, so use the same region where your account was created.
        </Typography>

        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          gap={{ xs: 1.2, sm: 2 }}
          sx={{ mt: 2.5 }}
        >
          <Typography
            component="a"
            href="https://nudgeforme.com/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              textDecoration: 'none',
              '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
            }}
          >
            Privacy
          </Typography>

          <Typography
            component="a"
            href="https://nudgeforme.com/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              textDecoration: 'none',
              '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
            }}
          >
            Terms of Use
          </Typography>

          <Typography
            component="a"
            href="mailto:hello@nudgeforme.com"
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              textDecoration: 'none',
              '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
            }}
          >
            Contact
          </Typography>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          sx={{ display: 'block', mt: 1.2, opacity: 0.82 }}
        >
          By continuing, you agree to the Terms of Use and acknowledge the Privacy Policy.
        </Typography>
      </Container>
    </Box>
  );
}
